// src/encounter/ips-family-history-dialog.tsx
// Add/Edit dialog for the IPS-rail Family History section. PRESENTATIONAL — composes DS Modal +
// CodeSearchField (relationship typeahead + per-row ICD-10 diagnosis) + TextField (per-row
// description) + DynamicDateInput (per-row From/To) + Button. No hooks/network: the host supplies
// both search result sets + the submit/search callbacks (mirrors IpsConditionDialog, but the form
// is MULTI-ROW — one relationship + an array of diagnosis rows). On submit it resolves the
// selection into a FamilyHistoryFormValue; the rail turns that into a wire request via
// buildFamilyHistoryRequest + useSaveFamilyHistory.
import { useState } from 'react';
import {
  Button,
  TextField,
  CodeSearchField,
  HelperText,
  type CodeSearchResult,
} from '@scripsteam/scrips-signal-ds';
import { AddSurface } from './add-surface';
import { DynamicDateInput } from '@/patients/components/chart/dynamic-date-input';
import type { ConditionCode, PatientFamilyHistory } from '@/types/patient-chart-api';
import type {
  FamilyHistoryFormValue,
  FamilyHistoryRelationship,
  FamilyHistoryRow,
} from '@/encounter/lib/to-family-history-request';

export interface IpsFamilyHistoryDialogProps {
  open: boolean;
  /** Present → Edit mode; absent → Add mode. */
  editing?: PatientFamilyHistory | null;

  // Relationship search (host-owned).
  relationshipQuery: string;
  onRelationshipQueryChange: (next: string) => void;
  relationshipResults: CodeSearchResult[];
  relationshipSearching?: boolean;
  /** Resolve a clicked relationship result → the coded relationship row. */
  resolveRelationship: (result: CodeSearchResult) => FamilyHistoryRelationship | null;

  // ICD-10 diagnosis search (host-owned, shared across rows).
  diagnosisQuery: string;
  onDiagnosisQueryChange: (next: string) => void;
  diagnosisResults: CodeSearchResult[];
  diagnosisSearching?: boolean;
  /** Resolve a clicked diagnosis result → the full ICD-10 ConditionCode row. */
  resolveDiagnosis: (result: CodeSearchResult) => ConditionCode | null;

  saving?: boolean;
  onSubmit: (value: FamilyHistoryFormValue) => void;
  onClose: () => void;
}

/** THE ruled field-label treatment (PROD-1801, Andrew 2 Aug) — shared, not re-declared.
 *  These six dialogs each carried a byte-identical local copy at xs/600/0.08em, which is a
 *  seventh idiom next to the four the rail popover already showed at once. The margin is the
 *  only local part: `AP_FIELD_LABEL` sets no spacing, and these labels sit above their field. */
// NOTE: AP_FIELD_LABEL lives in ap-dialog-label-style.tsx (out of scope for this pass), so its
// literal values (12/700/uppercase/0.01em/--color-text-hint) are reproduced here as the
// equivalent class string rather than imported as a style object.
const FIELD_LABEL =
  'pa-ips-label block text-size-eyebrow font-bold uppercase tracking-field-label text-text-hint mb-1';
const FIELD_GROUP = 'mb-4';
const SELECTED_CHIP =
  'pa-ips-chip flex items-center justify-between gap-2 py-2 px-3 border border-solid border-border-default rounded-md bg-surface-card';
const ROW_CARD =
  'pa-ips-card relative border border-solid border-border-default rounded-md p-4 mb-3';
/** Editable row state inside the dialog. `carry` round-trips an existing record's per-row
 *  recorder fields (undefined on a newly-added row). */
interface RowState {
  /** Stable local key — survives sibling removal. */
  key: string;
  code: ConditionCode | null;
  description: string;
  fromDisplay: string;
  fromIso: string | null;
  toDisplay: string;
  toIso: string | null;
  carry?: FamilyHistoryRow['carry'];
}

let rowCounter = 0;
function emptyRow(): RowState {
  rowCounter += 1;
  return { key: `fh-row-${rowCounter}`, code: null, description: '', fromDisplay: '', fromIso: null, toDisplay: '', toIso: null };
}

function rowsFromEditing(editing: PatientFamilyHistory): RowState[] {
  const rows = (editing.code ?? [])
    .filter((c) => c.isDeleted !== true)
    .map<RowState>((c) => {
      rowCounter += 1;
      return {
        key: `fh-row-${rowCounter}`,
        code: {
          code: c.code ?? null,
          display: c.display ?? null,
          system: c.system ?? null,
          description: c.description ?? null,
          Definition: null,
          shortName: null,
        },
        description: c.description ?? '',
        fromDisplay: c.fromDisplayTime ?? '',
        fromIso: c.fromTime ?? null,
        toDisplay: c.toDisplayTime ?? '',
        toIso: c.toTime ?? null,
        carry: {
          updatedby: c.updatedby ?? null,
          updatedDate: c.updatedDate ?? null,
          performerDetails: c.performerDetails ?? null,
          speciality: c.speciality ?? null,
        },
      };
    });
  return rows.length > 0 ? rows : [emptyRow()];
}

/** Outer gate + keyed remount; the inner form initialises state via lazy useState. */
export function IpsFamilyHistoryDialog(props: IpsFamilyHistoryDialogProps) {
  if (!props.open) return null;
  return <IpsFamilyHistoryDialogForm key={props.editing?.identifier ?? 'add'} {...props} />;
}

function IpsFamilyHistoryDialogForm({
  editing = null,
  relationshipQuery,
  onRelationshipQueryChange,
  relationshipResults,
  relationshipSearching,
  resolveRelationship,
  diagnosisQuery,
  onDiagnosisQueryChange,
  diagnosisResults,
  diagnosisSearching,
  resolveDiagnosis,
  saving,
  onSubmit,
  onClose,
}: IpsFamilyHistoryDialogProps) {
  const isEditing = !!editing?.identifier;
  const [relationship, setRelationship] = useState<FamilyHistoryRelationship | null>(() =>
    editing?.relationship
      ? {
          code: editing.relationship.code ?? null,
          name: editing.relationship.display ?? null,
          system: editing.relationship.system ?? null,
        }
      : null,
  );
  const [rows, setRows] = useState<RowState[]>(() => (editing ? rowsFromEditing(editing) : [emptyRow()]));
  /** Which row's diagnosis search is active — the shared diagnosis query routes to it. */
  const [activeDxRow, setActiveDxRow] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function patchRow(key: string, patch: Partial<RowState>) {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }
  function removeRow(key: string) {
    setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.key !== key) : rs));
  }
  function addRow() {
    setRows((rs) => [...rs, emptyRow()]);
  }

  function handleSubmit() {
    if (!relationship) {
      setError('Pick a relationship.');
      return;
    }
    if (rows.some((r) => !r.code)) {
      setError('Pick a diagnosis for every problem.');
      return;
    }
    if (rows.some((r) => !r.fromDisplay.trim() || !r.fromIso)) {
      setError('Each problem needs a parseable From date.');
      return;
    }
    if (rows.some((r) => r.toDisplay.trim() && !r.toIso)) {
      setError('A To date is not parseable.');
      return;
    }
    onSubmit({
      relationship,
      rows: rows.map<FamilyHistoryRow>((r) => ({
        code: r.code!,
        description: r.description.trim() || null,
        fromTime: r.fromIso,
        fromDisplay: r.fromDisplay,
        toTime: r.toIso,
        toDisplay: r.toDisplay.trim() ? r.toDisplay : null,
        carry: r.carry,
      })),
    });
  }

  return (
    <AddSurface
      open
      onClose={onClose}
      title={isEditing ? 'Edit family history' : 'Add family history'}
      size="lg"
      commit={{
        run: handleSubmit,
        label: 'Save family history',
        text: saving ? 'Saving…' : isEditing ? 'Save' : 'Add',
        busy: saving,
      }}
    >
      <div className="font-sans">
        {/* Relationship */}
        <div className={FIELD_GROUP}>
          <span className={FIELD_LABEL}>Relationship</span>
          {relationship ? (
            <div className={SELECTED_CHIP}>
              <span className="text-size-sm text-text-primary">
                {relationship.name ?? relationship.code ?? '—'}
              </span>
              <Button variant="tertiary" size="sm" onClick={() => setRelationship(null)} disabled={saving}>
                Change
              </Button>
            </div>
          ) : (
            <CodeSearchField
              value={relationshipQuery}
              onChange={onRelationshipQueryChange}
              results={relationshipResults}
              loading={relationshipSearching}
              placeholder="Search relationship (e.g. mother, father, sibling)…"
              onResultClick={(r) => {
                const picked = resolveRelationship(r);
                if (picked) {
                  setRelationship(picked);
                  setError(null);
                }
              }}
            />
          )}
        </div>

        {/* Problem rows */}
        {rows.map((row, idx) => (
          <div key={row.key} className={ROW_CARD}>
            <div className={FIELD_GROUP}>
              <span className={FIELD_LABEL}>Problem {idx + 1}</span>
              {row.code ? (
                <div className={SELECTED_CHIP}>
                  <span className="text-size-sm text-text-primary">
                    {row.code.display ?? row.code.code ?? '—'}
                    {row.code.code ? (
                      <span className="text-text-hint"> · {row.code.code}</span>
                    ) : null}
                  </span>
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => patchRow(row.key, { code: null })}
                    disabled={saving}
                  >
                    Change
                  </Button>
                </div>
              ) : (
                <CodeSearchField
                  value={activeDxRow === row.key ? diagnosisQuery : ''}
                  onChange={(next) => {
                    setActiveDxRow(row.key);
                    onDiagnosisQueryChange(next);
                  }}
                  results={activeDxRow === row.key ? diagnosisResults : []}
                  loading={activeDxRow === row.key && diagnosisSearching}
                  placeholder="Search ICD-10 diagnoses…"
                  onResultClick={(r) => {
                    const picked = resolveDiagnosis(r);
                    if (picked) {
                      patchRow(row.key, { code: picked });
                      setActiveDxRow(null);
                      onDiagnosisQueryChange('');
                      setError(null);
                    }
                  }}
                />
              )}
            </div>

            <div className={FIELD_GROUP}>
              <TextField
                label="Description"
                value={row.description}
                onChange={(e) => patchRow(row.key, { description: e.target.value })}
                placeholder="Enter description"
                maxLength={100}
                disabled={saving}
              />
            </div>

            <div className={`flex gap-3 ${FIELD_GROUP}`}>
              <div className="flex-1">
                <DynamicDateInput
                  label="From"
                  required
                  // A relative's onset is usually decades back — year dropdown.
                  captionLayout="dropdown"
                  displayValue={row.fromDisplay}
                  isoValue={row.fromIso}
                  onChange={(next) => patchRow(row.key, { fromDisplay: next.displayValue, fromIso: next.isoValue })}
                  disabled={saving}
                />
              </div>
              <div className="flex-1">
                <DynamicDateInput
                  label="To"
                  captionLayout="dropdown"
                  displayValue={row.toDisplay}
                  isoValue={row.toIso}
                  onChange={(next) => patchRow(row.key, { toDisplay: next.displayValue, toIso: next.isoValue })}
                  disabled={saving}
                />
              </div>
            </div>

            {rows.length > 1 ? (
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => removeRow(row.key)}
                disabled={saving}
                aria-label={`Remove problem ${idx + 1}`}
              >
                Remove problem
              </Button>
            ) : null}
          </div>
        ))}

        <Button variant="tertiary" size="sm" onClick={addRow} disabled={saving}>
          Add problem
        </Button>

        {error ? <HelperText tone="error">{error}</HelperText> : null}
      </div>
    </AddSurface>
  );
}
